import { Link } from 'react-router-dom';
import { Heart, Trash2, Play, BookOpen, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import useQuranStore from '../store/quranStore';

export default function FavoritesPage() {
  const { favorites, removeFavorite } = useQuranStore();

  // Sort by most recent
  const sortedFavorites = [...favorites].sort((a, b) => b.timestamp - a.timestamp);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" data-testid="favorites-page">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-red-500" />
            </div>
            <h1 className="font-serif text-3xl font-bold">Favorite Surahs</h1>
          </div>
          <p className="text-muted-foreground">
            {favorites.length} surah{favorites.length !== 1 ? 's' : ''} favorited
          </p>
        </div>

        {/* Empty state */}
        {favorites.length === 0 && (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-semibold mb-2">No favorites yet</h3>
            <p className="text-muted-foreground mb-6">
              Mark surahs as favorites for quick access
            </p>
            <Link to="/">
              <Button data-testid="browse-surahs-btn">
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Surahs
              </Button>
            </Link>
          </div>
        )}

        {/* Favorites grid */}
        {favorites.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedFavorites.map((surah, index) => (
              <div
                key={surah.surahNo}
                className="group relative bg-card rounded-xl border p-5 hover:border-primary/50 hover:-translate-y-1 transition-all"
                data-testid={`favorite-item-${index}`}
              >
                {/* Surah number badge */}
                <div className="absolute -top-3 -left-2 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
                  <span className="text-sm font-semibold text-primary">{surah.surahNo}</span>
                </div>

                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                  onClick={() => removeFavorite(surah.surahNo)}
                  data-testid={`remove-favorite-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>

                {/* Content */}
                <div className="mt-4">
                  {/* Arabic name */}
                  <h3 className="font-arabic text-2xl text-right mb-2">
                    {surah.surahNameArabic}
                  </h3>
                  
                  {/* English name */}
                  <h4 className="font-semibold mb-1">{surah.surahName}</h4>
                  
                  {/* Date added */}
                  <p className="text-xs text-muted-foreground mb-4">
                    Added {formatDate(surah.timestamp)}
                  </p>

                  {/* Action */}
                  <Link to={`/surah/${surah.surahNo}`}>
                    <Button className="w-full gap-2" data-testid={`read-favorite-${index}`}>
                      <Play className="w-4 h-4" />
                      Read Surah
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
